/**
 * A list of supported interpolator types.
 *
 * Only Linear is currently supported (and needed) but
 * other interpolators may use other curves.
 */
export type InterpolationType = 'linear';

/**
 * A linear Interpolater between two numbers
 * @param from The starting number
 * @param to The ending number
 * @param percentage A number between 0 and 1 that represents the interpolated
 * point between the start and the end
 * @returns A number that is between the from and to points given the percentage provided.
 */
export const linearInterpolator = (
    from: number,
    to: number,
    percentage: number,
) => from + (to - from) * percentage;

/**
 * Handles the animating from one number to another number using a provided interpolator.
 */
export default class Interpolator {
    private frameRef?: number;

    private elapsedTime = 0;

    private previousTime?: number;

    private animating = false;

    private from = NaN;

    private to = NaN;

    private onComplete?: () => void;

    private resetting = false;

    /**
     *
     * @param duration The time in milliseconds it takes to move from
     * the start point to the end point.
     * @param callback A callback for each animation 'tick'.
     * Return false to cancel the animation and reset it.
     * @param type The type of interpolation curve to use
     */
    constructor(
        public duration: number,
        private callback: (
            value: number,
            animator: Interpolator,
        ) => boolean | void,
        private type: InterpolationType = 'linear',
    ) {}

    public getElapsedTime = () => this.elapsedTime;

    private tick(timestamp: number) {
        this.elapsedTime += timestamp - (this.previousTime ?? timestamp);

        if (this.elapsedTime > 0) {
            if (this.elapsedTime >= this.duration) {
                this.reset();
                return;
            }

            const percentage = this.elapsedTime / this.duration;

            let value = NaN;
            if (this.type === 'linear') {
                value = linearInterpolator(this.from, this.to, percentage);
            }

            // For other types of interpolator, add them here

            const result = this.callback(value, this);
            if (result === false) {
                this.reset();
                return;
            }
        }

        if (this.animating) {
            this.previousTime = timestamp;
            this.frameRef = window.requestAnimationFrame((ts) => this.tick(ts));
        }
    }

    public start(from?: number, to?: number, onComplate?: () => void) {
        if (from != null) this.from = from;
        if (to != null) this.to = to;
        if (onComplate) this.onComplete = onComplate;

        this.animating = true;
        this.frameRef = window.requestAnimationFrame((ts) => this.tick(ts));
    }

    public pause() {
        this.animating = false;
        this.previousTime = undefined;
        if (this.frameRef != null) window.cancelAnimationFrame(this.frameRef);
    }

    public reset() {
        if (!this.resetting) {
            this.resetting = true;
            if (this.onComplete) this.onComplete();
            this.pause();
            this.onComplete = undefined;
            this.elapsedTime = 0;
            this.resetting = false;
        }
    }
}
